import { apiSlice } from "../api/apiSlice";
import { concatPathWithParams } from "../api/apiHelper";

// Field Admin's endpoints, injected into the shared api slice rather than declared in it.
//
// Installer-CLAUDE.md records "one monolithic apiSlice, zero injectEndpoints" as the
// house rule, and this feature followed it — but Field Admin had grown to 43 of the 84
// endpoints and 18 of the 40 tag types, so more than half of a file shared with Installer,
// QE and DSM was one feature's. The sibling DSM repo already uses injectEndpoints for
// exactly this reason.
//
// The split is transparent to callers: same cache, same store slice, same hook names —
// only the import path changes. enhanceEndpoints is required before injection because
// injectEndpoints cannot introduce new tag types on its own.
const fieldAdminApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "fa_sites",
      "fa_site_detail",
      "fa_stock_balance",
      "fa_hs_day",
      "fa_hs_vehicle",
      "fa_profile",
      "fa_alerts",
      "fa_stock_closeout",
      "fa_hs_tools",
      "fa_hs_attendance",
      "fa_hs_toolbox",
      "fa_hs_devices",
      "fa_hs_history",
      "fa_qe_oversight",
      "fa_vehicles",
      "fa_team",
      "fa_team_conduct_flags",
      "fa_devices",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getFieldAdminSites: builder.query({
        query: () => "/field-admin/sites/",
        providesTags: ["fa_sites"],
      }),

      getFieldAdminSiteDetail: builder.query({
        query: ({ code }) => `/field-admin/sites/${encodeURIComponent(code)}/`,
        providesTags: ["fa_site_detail"],
      }),

      getFieldAdminStockBalance: builder.query({
        query: () => "/field-admin/stock/balance/",
        providesTags: ["fa_stock_balance"],
      }),

      collectFieldAdminStock: builder.mutation({
        query: (body) => ({
          url: "/field-admin/stock/collect/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_stock_balance"],
      }),

      // What is physically in the van, for the distribute picker. Invalidated by every
      // stock write, so issuing removes the device from the list without a reload.
      getFieldAdminStockOnVan: builder.query({
        query: () => "/field-admin/stock/on-van/",
        // The same tag the three stock writes invalidate, so collecting, issuing or
        // returning a device re-reads the van without its own bookkeeping.
        providesTags: ["fa_stock_balance"],
      }),

      issueFieldAdminStock: builder.mutation({
        query: (body) => ({
          url: "/field-admin/stock/issue/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_stock_balance"],
      }),

      // Today's flagged returns, for the returns screen's second tab. Shares the tag the
      // stock writes invalidate, so logging a return updates the tab count immediately.
      getFieldAdminStockReturns: builder.query({
        query: () => "/field-admin/stock/returns/",
        providesTags: ["fa_stock_balance"],
      }),

      returnFieldAdminStock: builder.mutation({
        query: (body) => ({
          url: "/field-admin/stock/return/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_stock_balance"],
      }),

      getFieldAdminHSDay: builder.query({
        query: () => "/field-admin/hs/day/",
        providesTags: ["fa_hs_day"],
      }),

      // The three day-session transitions. All invalidate fa_hs_history as well as the
      // day itself: the history list renders each day's status, so a sign-off or a reopen
      // changes what it shows. Completing never refreshed it before.
      startFieldAdminHSDay: builder.mutation({
        query: () => ({
          url: "/field-admin/hs/day/start/",
          method: "POST",
        }),
        invalidatesTags: ["fa_hs_day", "fa_hs_history"],
      }),

      completeFieldAdminHSDay: builder.mutation({
        query: () => ({
          url: "/field-admin/hs/day/complete/",
          method: "POST",
        }),
        invalidatesTags: ["fa_hs_day", "fa_hs_history"],
      }),

      reopenFieldAdminHSDay: builder.mutation({
        query: () => ({
          url: "/field-admin/hs/day/reopen/",
          method: "POST",
        }),
        invalidatesTags: ["fa_hs_day", "fa_hs_history"],
      }),

      getFieldAdminHSHistory: builder.query({
        query: (queryParams) => concatPathWithParams("/field-admin/hs/history/", queryParams),
        providesTags: ["fa_hs_history"],
      }),

      getFieldAdminHSVehicle: builder.query({
        query: () => "/field-admin/hs/vehicle/",
        providesTags: ["fa_hs_vehicle"],
      }),

      saveFieldAdminHSVehicle: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/vehicle/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_hs_vehicle", "fa_hs_day"],
      }),

      uploadFieldAdminHSVehiclePhoto: builder.mutation({
        query: ({ slot, file }) => {
          const formData = new FormData();
          formData.append("slot", slot);
          formData.append("image", file);
          return {
            url: "/field-admin/hs/vehicle/photo/",
            method: "POST",
            body: formData,
          };
        },
        invalidatesTags: ["fa_hs_vehicle"],
      }),

      getFieldAdminHSTools: builder.query({
        query: () => "/field-admin/hs/tools/",
        providesTags: ["fa_hs_tools"],
      }),

      saveFieldAdminHSTool: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/tools/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_hs_tools", "fa_hs_day"],
      }),

      getFieldAdminHSAttendance: builder.query({
        query: () => "/field-admin/hs/attendance/",
        providesTags: ["fa_hs_attendance"],
      }),

      saveFieldAdminHSAttendance: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/attendance/",
          method: "POST",
          body,
        }),
        // A conduct flag is raised from this step but read on the Team page, whose query
        // provides fa_team_conduct_flags. Nothing invalidated that tag, so a flag recorded
        // here never showed up there until the cache happened to expire.
        invalidatesTags: ["fa_hs_attendance", "fa_hs_day", "fa_team_conduct_flags"],
      }),

      getFieldAdminHSToolbox: builder.query({
        query: () => "/field-admin/hs/toolbox/",
        providesTags: ["fa_hs_toolbox"],
      }),

      getFieldAdminProfile: builder.query({
        query: () => "/field-admin/profile/",
        providesTags: ["fa_profile"],
      }),

      saveFieldAdminProfile: builder.mutation({
        query: (body) => ({
          url: "/field-admin/profile/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_profile"],
      }),

      saveFieldAdminSiteNotes: builder.mutation({
        query: ({ code, notes }) => ({
          url: `/field-admin/sites/${encodeURIComponent(code)}/`,
          method: "POST",
          body: { notes },
        }),
        invalidatesTags: ["fa_sites"],
      }),

      getFieldAdminAlerts: builder.query({
        query: () => "/field-admin/alerts/",
        // Derived from the same rows every other Field Admin page writes, so any of those
        // mutations can change the feed — refetch on focus keeps the badge honest.
        providesTags: ["fa_alerts"],
      }),

      // Read/cleared state for one alert, or read for a batch. Keys come straight back
      // from the feed — never built client-side, since a key encodes the alert's contents
      // and a constructed one would go stale the moment those changed.
      setFieldAdminAlertState: builder.mutation({
        query: (body) => ({
          url: "/field-admin/alerts/state/",
          method: "POST",
          body,
        }),
        // fa_alerts had no invalidator at all until now, so the badge and the feed only
        // refreshed on a full remount.
        invalidatesTags: ["fa_alerts"],
      }),

      getFieldAdminStockCloseOut: builder.query({
        query: () => "/field-admin/stock/closeout/",
        providesTags: ["fa_stock_closeout"],
      }),

      saveFieldAdminStockCloseOut: builder.mutation({
        query: (body) => ({
          url: "/field-admin/stock/closeout/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_stock_closeout"],
      }),

      saveFieldAdminHSSectionNotes: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/section-notes/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_hs_tools", "fa_hs_devices"],
      }),

      saveFieldAdminHSToolboxTalk: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/toolbox/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_hs_toolbox", "fa_hs_day"],
      }),

      acknowledgeFieldAdminHSToolbox: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/toolbox/acknowledge/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_hs_toolbox", "fa_hs_day"],
      }),

      getFieldAdminHSDevices: builder.query({
        query: () => "/field-admin/hs/devices/",
        providesTags: ["fa_hs_devices"],
      }),

      saveFieldAdminHSDevice: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/devices/",
          method: "POST",
          body,
        }),
        // fa_devices too: the daily reading is what the Devices register's "Last
        // reading" column shows, so without this it goes stale on every save.
        invalidatesTags: ["fa_hs_devices", "fa_hs_day", "fa_devices"],
      }),

      // Cover facts for the export page's preview. Range-scoped, so it re-reads as the
      // dates change and the panel always describes the bundle currently configured.
      getFieldAdminHSExportPreview: builder.query({
        query: (queryParams) => concatPathWithParams("/field-admin/hs/export/", queryParams),
        providesTags: ["fa_hs_day"],
      }),

      exportFieldAdminHSPdf: builder.mutation({
        query: (body) => ({
          url: "/field-admin/hs/export/",
          method: "POST",
          body,
          responseHandler: (response) => response.blob(),
        }),
        transformResponse: (blob, meta) => {
          const disposition = meta?.response?.headers.get("content-disposition") || "";
          const match = disposition.match(/filename=([^;]+)/);
          return { blob, filename: match ? match[1] : "HS-export.pdf" };
        },
      }),

      getFieldAdminQEOversight: builder.query({
        query: () => "/field-admin/qe/oversight/",
        providesTags: ["fa_qe_oversight"],
      }),

      promptFieldAdminQE: builder.mutation({
        query: (body) => ({
          url: "/field-admin/qe/prompt/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_qe_oversight"],
      }),

      getFieldAdminVehicles: builder.query({
        query: () => "/field-admin/vehicles/",
        providesTags: ["fa_vehicles"],
      }),

      saveFieldAdminVehicle: builder.mutation({
        query: (body) => ({
          url: "/field-admin/vehicles/save/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_vehicles"],
      }),

      setFieldAdminVehicleStatus: builder.mutation({
        query: ({ vehicle_id, ...body }) => ({
          url: `/field-admin/vehicles/${vehicle_id}/status/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_vehicles"],
      }),

      getFieldAdminTeamOverview: builder.query({
        query: () => "/field-admin/team/overview/",
        providesTags: ["fa_team"],
      }),

      getFieldAdminTeamInstallers: builder.query({
        query: () => "/field-admin/team/installers/",
        providesTags: ["fa_team"],
      }),

      saveFieldAdminTeamInstaller: builder.mutation({
        query: (body) => ({
          url: "/field-admin/team/installers/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_team"],
      }),

      setFieldAdminTeamInstallerStatus: builder.mutation({
        query: ({ installer_id, ...body }) => ({
          url: `/field-admin/team/installers/${installer_id}/status/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_team"],
      }),

      getFieldAdminTeamConductFlags: builder.query({
        query: (queryParams) => concatPathWithParams("/field-admin/team/conduct-flags/", queryParams),
        providesTags: ["fa_team_conduct_flags"],
      }),

      getFieldAdminDevices: builder.query({
        query: () => "/field-admin/devices/",
        providesTags: ["fa_devices"],
      }),

      saveFieldAdminDevice: builder.mutation({
        query: (body) => ({
          url: "/field-admin/devices/save/",
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_devices"],
      }),

      setFieldAdminDeviceStatus: builder.mutation({
        query: ({ device_id, ...body }) => ({
          url: `/field-admin/devices/${device_id}/status/`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["fa_devices"],
      }),

    }),
  });

export const {
  useGetFieldAdminSitesQuery,
  useGetFieldAdminSiteDetailQuery,
  useGetFieldAdminStockBalanceQuery,
  useCollectFieldAdminStockMutation,
  useGetFieldAdminStockOnVanQuery,
  useIssueFieldAdminStockMutation,
  useGetFieldAdminStockReturnsQuery,
  useReturnFieldAdminStockMutation,
  useGetFieldAdminHSDayQuery,
  useCompleteFieldAdminHSDayMutation,
  useStartFieldAdminHSDayMutation,
  useReopenFieldAdminHSDayMutation,
  useGetFieldAdminHSHistoryQuery,
  useGetFieldAdminHSVehicleQuery,
  useSaveFieldAdminHSVehicleMutation,
  useUploadFieldAdminHSVehiclePhotoMutation,
  useGetFieldAdminHSToolsQuery,
  useSaveFieldAdminHSToolMutation,
  useGetFieldAdminHSAttendanceQuery,
  useSaveFieldAdminHSAttendanceMutation,
  useGetFieldAdminHSToolboxQuery,
  useGetFieldAdminProfileQuery,
  useSaveFieldAdminProfileMutation,
  useSaveFieldAdminSiteNotesMutation,
  useGetFieldAdminAlertsQuery,
  useSetFieldAdminAlertStateMutation,
  useGetFieldAdminStockCloseOutQuery,
  useSaveFieldAdminStockCloseOutMutation,
  useSaveFieldAdminHSSectionNotesMutation,
  useSaveFieldAdminHSToolboxTalkMutation,
  useAcknowledgeFieldAdminHSToolboxMutation,
  useGetFieldAdminHSDevicesQuery,
  useSaveFieldAdminHSDeviceMutation,
  useGetFieldAdminHSExportPreviewQuery,
  useExportFieldAdminHSPdfMutation,
  useGetFieldAdminQEOversightQuery,
  usePromptFieldAdminQEMutation,
  useGetFieldAdminVehiclesQuery,
  useSaveFieldAdminVehicleMutation,
  useSetFieldAdminVehicleStatusMutation,
  useGetFieldAdminTeamOverviewQuery,
  useGetFieldAdminTeamInstallersQuery,
  useSaveFieldAdminTeamInstallerMutation,
  useSetFieldAdminTeamInstallerStatusMutation,
  useGetFieldAdminTeamConductFlagsQuery,
  useGetFieldAdminDevicesQuery,
  useSaveFieldAdminDeviceMutation,
  useSetFieldAdminDeviceStatusMutation,
} = fieldAdminApi;
